import {
  ChangeDetectionStrategy,
  Component,
  Inject,
  OnDestroy,
  OnInit,
} from "@angular/core";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { DataAccessService } from "@kells/apis/data-access";
import { SubscriptionPlan } from "@kells/interfaces/patient";
import { BehaviorSubject, combineLatest } from "rxjs";
import { SubSink } from "subsink";
import { map } from "rxjs/operators";
import { environment } from "apps/patient-reporting/src/environments/environment";
import { AnalyticsService } from "@kells/apis/analytics";
import { MEMBER_PROVIDERS_LINK } from "../../constants";
import {
  BackToPlansClick,
  CloseDentalProviderUpgradePlanModal,
  OpenDentalProviderUpgradePlanModal,
  PreviewProviderNetworkClick,
  ShowAddressSection,
} from "apps/patient-reporting/src/app/shared/models/analytics/home-events";

enum ModalMode {
  ProviderPreview = "provider-preview",
  Address = "address",
  Plans = "plans",
}

const ModalModeToEventMap = {
  [ModalMode.ProviderPreview]: PreviewProviderNetworkClick,
  [ModalMode.Address]: ShowAddressSection,
  [ModalMode.Plans]: BackToPlansClick,
};

export interface UpgradePlanModalData {
  title?: string;
  description?: string;
  selectedPlan?: SubscriptionPlan;
  availableUpgrades?: SubscriptionPlan[];
}

interface ProviderPreview {
  icon: string;
  image: string;
  title: string;
  description: string;
  class?: string;
}

@Component({
  selector: "koa-dental-provider-upgrade-plan-modal",
  templateUrl: "./dental-provider-upgrade-plan-modal.component.html",
  styleUrls: ["./dental-provider-upgrade-plan-modal.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DentalProviderUpgradePlanModalComponent
  implements OnInit, OnDestroy {
  public readonly MEMBER_PROVIDERS_LINK = MEMBER_PROVIDERS_LINK;

  public readonly ModalMode = ModalMode;

  description = "";
  selectedPlan: SubscriptionPlan = SubscriptionPlan.Complete;
  availableUpgrades = [SubscriptionPlan.Complete];

  public readonly title$ = new BehaviorSubject<string>("");

  public readonly mode$ = new BehaviorSubject<ModalMode>(ModalMode.Plans);

  public readonly modalTitle$ = combineLatest([this.title$, this.mode$]).pipe(
    map(([title, mode]) =>
      mode === ModalMode.Address
        ? "Upgrade Your Plan | KELLS Complete Plan"
        : title
    )
  );

  public readonly isSelectingPlan$ = new BehaviorSubject<boolean>(false);

  public readonly completePlanProduct$ = this.dataService.products$.pipe(
    map((products) =>
      products.find(
        (product) =>
          product.product_id === environment.stripeCompletePlanProductId
      )
    )
  );

  private _subs = new SubSink();

  public readonly providerPreviews: ProviderPreview[] = [
    {
      icon: "assets/search.svg",
      image: "assets/find-dentist-search-zip.png",
      title: "Location Based Search",
      description: "Looking for dentists in your area? Look no further.",
      class: "location-based-search",
    },
    {
      icon: "assets/browse.svg",
      image: "assets/find-dentist-tier.png",
      title: "Browse Provider Details",
      description:
        "See key details about each provider from training to specialties.",
      class: "browse-provider-details",
    },
    {
      icon: "assets/pricing.svg",
      image: "assets/find-dentist-pricing.png",
      title: "Transparent Upfront Pricing",
      description:
        "Get a clear estimate of how much you'll pay for the services you obtain.",
      class: "transparent-upfront-pricing",
    },
  ];

  constructor(
    public dialogRef: MatDialogRef<DentalProviderUpgradePlanModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: UpgradePlanModalData,
    private readonly dataService: DataAccessService,
    private readonly analyticsService: AnalyticsService
  ) {
    this.title$.next(
      this.data?.title ?? "Upgrade Your Plan | Dental Provider Network"
    );
    this.description =
      this.data?.description ??
      "Upgrade your plan today to access our National Dental Provider Network with discounted rates.";
    this.availableUpgrades = this.data?.availableUpgrades ?? [
      SubscriptionPlan.Complete,
    ];
    this.selectedPlan = this.data?.selectedPlan ?? SubscriptionPlan.Complete;
  }

  ngOnInit(): void {
    this.analyticsService.record(OpenDentalProviderUpgradePlanModal({}));
  }

  ngOnDestroy() {
    this._subs.unsubscribe();
  }

  public closeDialog() {
    this.analyticsService.record(CloseDentalProviderUpgradePlanModal({}));
    this.dialogRef.close();
  }

  public onPreviewProviderNetworkBackClick(event: Event) {
    event.preventDefault();
    this.setMode(ModalMode.Plans);
  }

  public setMode(mode: ModalMode) {
    this.mode$.next(mode);
    this.analyticsService.record(ModalModeToEventMap[mode]({}));
  }
}
